import React, { useState } from 'react';
import Button from '@sizeid/commons/components/Button';
import { nlBr } from '@sizeid/commons/utils/strings';
import { WithTranslation } from 'next-i18next';

import H2 from 'components/typo/Heading/H2';
import H3 from 'components/typo/Heading/H3';
import P from 'components/typo/P';
import Collapse from 'components/Collapse';
import { withTranslation } from 'lib/i18n';

import graphConversionImage from '../images/Graph_02_conversion@2x.png';
import graphExchangeImage from '../images/Graph_01_exhange of goods@2x.png';

import Reference from './Reference';
import Costs from './Costs';
import Graph from './Graph';
import Halves from './Halves';
import Quote from './Quote';
import SupportIcons from './SupportIcons';

const BusinessCaseStudy: React.FC<WithTranslation> = ({ t }) => {
  const [collapsed, setCollapsed] = useState(true);

  const toggleCollapsed = (
    e: React.MouseEvent<Element, MouseEvent> | null,
  ): void => {
    if (e) {
      e.preventDefault();
    }
    setCollapsed(!collapsed);
  };

  const renderGraphLegend = (
    graph: 'exchange' | 'conversion',
  ): React.ReactNode =>
    ['red', 'gray'].map(color => (
      <li key={color}>
        <span className={color} />
        {t(`business.index.case_study.${graph}.${color}`)}
      </li>
    ));

  return (
    <>
      <H2>{t('business.index.case_study.title')}</H2>
      <P dark bigOnDesktop lead>
        {nlBr(t('business.index.case_study.description'))}
      </P>
      <Halves>
        <div>
          <H3>{t('business.index.case_study.exchange.title')}</H3>
          <P>{t('business.index.case_study.exchange.above_graph')}</P>
          <Graph>
            <img src={graphExchangeImage} alt="Exchange" />
            <ul>{renderGraphLegend('exchange')}</ul>
          </Graph>
          <P>{t('business.index.case_study.exchange.under_graph')}</P>
          <Collapse collapsed={collapsed}>
            <>
              <H3>{t('business.index.case_study.conversion.title')}</H3>
              <P>{t('business.index.case_study.conversion.above_graph')}</P>
              <Graph>
                <img src={graphConversionImage} alt="Conversion" />
                <ul>{renderGraphLegend('conversion')}</ul>
              </Graph>
              <P>{t('business.index.case_study.conversion.under_graph')}</P>
              <H3>{t('business.index.case_study.economics.title')}</H3>
              <P>{t('business.index.case_study.economics.paragraph_top')}</P>
              <Costs />
              <P>
                {t('business.index.case_study.economics.paragraph_bottom_1')}
              </P>
              <P>
                {t('business.index.case_study.economics.paragraph_bottom_2')}
              </P>
              <P>
                {t('business.index.case_study.economics.paragraph_bottom_3')}
              </P>
            </>
          </Collapse>
        </div>
        <div>
          <Reference />
          <Collapse collapsed={collapsed}>
            <>
              <H3>{t('business.index.case_study.integration.title')}</H3>
              <P>{t('business.index.case_study.integration.above_quotes')}</P>
              <Quote base="business.index.case_study.integration.quote_1" />
              <P>{t('business.index.case_study.integration.between_quotes')}</P>
              <Quote base="business.index.case_study.integration.quote_2" />
              <H3>{t('business.index.case_study.support.title')}</H3>
              <P>{t('business.index.case_study.support.paragraph_1')}</P>
              <SupportIcons />
              <P>{t('business.index.case_study.support.paragraph_2')}</P>
              <P>{t('business.index.case_study.support.paragraph_3')}</P>
            </>
          </Collapse>
        </div>
      </Halves>
      <Button large outline onClick={toggleCollapsed}>
        {t(collapsed ? 'business.index.show_more' : 'business.index.show_less')}
      </Button>
    </>
  );
};

export default withTranslation('pages')(BusinessCaseStudy);
